import React from 'react';

const DataStatusPanel = ({ stats, onRefresh }) => {
  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const getHealthStatus = (rawCount, analyzedCount) => {
    if (rawCount === 0 && analyzedCount === 0) {
      return { status: 'empty', color: 'red', text: 'No Data' };
    }
    if (rawCount > 0 && analyzedCount === 0) {
      return { status: 'processing', color: 'yellow', text: 'Processing Required' };
    }
    if (rawCount === analyzedCount && rawCount > 0) {
      return { status: 'healthy', color: 'green', text: 'Healthy' };
    }
    return { status: 'partial', color: 'orange', text: 'Partial' };
  };

  // Extract stats from the new comprehensive stats structure
  const pineconeStats = stats.pinecone || {};
  const localDbStats = stats.local_database || {};
  const overallStats = stats.overall || {};
  
  const pineconeRawCount = pineconeStats.raw_data_index?.total_vector_count || 0;
  const pineconeAnalyzedCount = pineconeStats.analyzed_data_index?.total_vector_count || 0;
  const localRawCount = localDbStats.total_raw_articles || 0;
  const localAnalyzedCount = localDbStats.total_analyzed_articles || 0;
  
  // Use the higher count between Pinecone and local DB
  const totalRawCount = Math.max(pineconeRawCount, localRawCount);
  const totalAnalyzedCount = Math.max(pineconeAnalyzedCount, localAnalyzedCount);

  const health = getHealthStatus(totalRawCount, totalAnalyzedCount);

  return (
    <div className="space-y-6">
      {/* Health Status */}
      <div className="text-center">
        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-${health.color}-100 text-${health.color}-800 dark:bg-${health.color}-900/20 dark:text-${health.color}-400`}>
          <div className={`w-2 h-2 rounded-full bg-${health.color}-500 mr-2`}></div>
          {health.text}
        </div>
      </div>

      {/* Overall Statistics Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Raw Data */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Raw Data</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(totalRawCount)}
              </p>
            </div>
            <div className="text-3xl">📄</div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Articles stored
          </p>
        </div>

        {/* Analyzed Data */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Analyzed Data</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(totalAnalyzedCount)}
              </p>
            </div>
            <div className="text-3xl">🧠</div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            NLP processed
          </p>
        </div>
      </div>

      {/* Storage Systems Status */}
      <div className="grid grid-cols-1 gap-4">
        {/* Pinecone Status */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 flex items-center gap-2">
                <span className="text-lg">🔗</span>
                Pinecone Vector Database
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Raw: {formatNumber(pineconeRawCount)} | Analyzed: {formatNumber(pineconeAnalyzedCount)}
              </p>
            </div>
            <div className={`w-3 h-3 rounded-full ${pineconeStats.error ? 'bg-red-500' : 'bg-green-500'}`}></div>
          </div>
          {pineconeStats.error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Error: {pineconeStats.error}
            </p>
          )}
        </div>

        {/* Local Database Status */}
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-200 flex items-center gap-2">
                <span className="text-lg">💾</span>
                Local SQLite Database
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                Raw: {formatNumber(localRawCount)} | Analyzed: {formatNumber(localAnalyzedCount)}
              </p>
            </div>
            <div className={`w-3 h-3 rounded-full ${localDbStats.error ? 'bg-red-500' : 'bg-green-500'}`}></div>
          </div>
          {localDbStats.error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Error: {localDbStats.error}
            </p>
          )}
        </div>
      </div>

      {/* Processing Status */}
      {totalRawCount > 0 && (
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-purple-800 dark:text-purple-200">
                Processing Status
              </h4>
              <p className="text-sm text-purple-700 dark:text-purple-300">
                {totalAnalyzedCount === totalRawCount 
                  ? 'All data has been analyzed'
                  : `${totalAnalyzedCount} of ${totalRawCount} articles analyzed`
                }
              </p>
            </div>
            <div className="text-2xl">⚙️</div>
          </div>
          
          {totalAnalyzedCount < totalRawCount && (
            <div className="mt-3">
              <div className="w-full bg-purple-200 dark:bg-purple-700 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(totalAnalyzedCount / totalRawCount) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                {Math.round((totalAnalyzedCount / totalRawCount) * 100)}% complete
              </p>
            </div>
          )}
        </div>
      )}

      {/* Last Updated */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-gray-200">
              Last Updated
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {stats.lastUpdated || 'Never'}
            </p>
          </div>
          <button
            onClick={onRefresh}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Refresh stats"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="space-y-2">
        <button
          onClick={onRefresh}
          className="w-full py-2 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
        >
          🔄 Refresh Statistics
        </button>
      </div>

      {/* System Info */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2">
          System Information
        </h4>
        <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1">
          <li>• Vector Database: Pinecone (Cloud)</li>
          <li>• Local Database: SQLite (critical_minerals.db)</li>
          <li>• Tables: raw_articles, analyzed_articles</li>
          <li>• NLP Engine: Critical Minerals Analyzer</li>
          <li>• Embeddings: Sentence Transformers</li>
        </ul>
      </div>
    </div>
  );
};

export default DataStatusPanel; 